import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import './UserProfile.css';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pseudo, setPseudo] = useState('');
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          setPseudo(userDoc.data().pseudo || '');
          setIsFirstLogin(false);
        } else {
          setPseudo('');
          setIsFirstLogin(true);
          setIsEditing(true); // Enable editing for first login
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const validatePseudo = (value) => {
    if (!value) return 'Le pseudo est requis';
    if (value.length < 3) return 'Le pseudo doit contenir au moins 3 caractères';
    if (value.length > 20) return 'Le pseudo ne peut pas dépasser 20 caractères';
    return '';
  };

  const handlePseudoChange = (event) => {
    const newPseudo = event.target.value;
    setPseudo(newPseudo);
    setError(validatePseudo(newPseudo));
  };

  const handleSavePseudo = async () => {
    const validationError = validatePseudo(pseudo);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const userDocRef = doc(db, "users", user.uid);
      await setDoc(userDocRef, { pseudo }, { merge: true });
      setSuccess('Pseudo enregistré avec succès !');
      setError('');
      setIsFirstLogin(false);
      setIsEditing(false); // Exit edit mode after saving
      setTimeout(() => setSuccess(''), 3000); // Clear success message after 3 seconds
    } catch (error) {
      setError('Erreur lors de l\'enregistrement du pseudo : ' + error.message);
      setSuccess('');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      setError('Erreur lors de la déconnexion : ' + error.message);
    }
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className="loading">
        <span className="loading-title">
          <span>C</span>
          <span className="rotating">h</span>
          <span>a</span>
          <span>r</span>
          <span>g</span>
          <span>e</span>
          <span>m</span>
          <span>e</span>
          <span>n</span>
          <span>t</span>
        </span>
      </div>
    );
  }

  return (
    <div className="profile-section">
      <h2 className="account-title">Profil Utilisateur</h2>
      {user ? (
        <div className="profile-card dashboard-card">
          <div className="profile-edit" style={{ position: 'relative' }}>
            {success && (
              <p
                className="success-message"
                style={{
                  position: 'absolute',
                  top: '-2rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'var(--success)',
                  fontSize: '0.85rem',
                  background: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-md)',
                  width: '200px',
                  textAlign: 'center',
                  animation: 'fadeInUp var(--transition-fast) ease forwards',
                  zIndex: 10
                }}
              >
                {success}
              </p>
            )}
            <h3>Informations Personnelles</h3>
            <div className="form-group">
              <label>Email</label>
              <input
                type="text"
                value={user.email}
                readOnly
                disabled
                className="form-group input"
              />
            </div>
            <div className="form-group">
              <label>Pseudo {isFirstLogin && <span style={{ color: 'var(--danger)' }}>(Requis)</span>}</label>
              <input
                type="text"
                value={pseudo}
                onChange={handlePseudoChange}
                placeholder="Entrez votre pseudo"
                className={`form-group input ${error ? 'error' : ''}`}
                readOnly={!isEditing}
                disabled={!isEditing}
              />
              {error && (
                <p
                  className="error-message"
                  style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: '0.5rem' }}
                >
                  {error}
                </p>
              )}
            </div>
            <div className="button-group">
              {!isEditing && !isFirstLogin && (
                <button
                  className="save-btn"
                  onClick={handleEditProfile}
                >
                  Modifier le Profil
                </button>
              )}
              {(isEditing || isFirstLogin) && (
                <button
                  className="save-btn"
                  onClick={handleSavePseudo}
                  disabled={!!error || !pseudo}
                >
                  Enregistrer
                </button>
              )}
              <button
                className="cancel-btn"
                onClick={handleSignOut}
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-data-message">
          <p>Aucun utilisateur connecté.</p>
          <p>Veuillez vous connecter pour accéder à votre profil.</p>
        </div>
      )}
    </div>
  );
};

export default UserProfile;